import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api, { API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { CheckCircle, Star, Play, Pause, ShoppingCart, Sun, Moon, ArrowRight, ArrowLeft, Headphones, Volume2, Clock, Shield, Sparkles, ExternalLink, Search, CreditCard, BookOpen, Zap, FileText, Package } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { getDisplayPrice } from '@/lib/priceUtils';
import { PriceButton, PriceDisplay } from '@/components/PriceDisplay';
import { durationColor } from '@/lib/badgeColors';
import Footer from '@/components/Footer';

function RenderSection({ section, product, allProducts, allSections, onCheckout, teaserRef, playingTeaser, setPlayingTeaser }) {
  if (!section.enabled) return null;
  const f = section.fields || {};

  switch (section.type) {
    case 'hero': {
      // Pick random headline/subheadline from arrays
      const headlines = (f.headlines || []).filter(h => h?.trim());
      const subheadlines = (f.subheadlines || []).filter(s => s?.trim());
      const headline = headlines.length > 0 ? headlines[Math.floor(Math.random() * headlines.length)] : f.headline || '';
      const subheadline = subheadlines.length > 0 ? subheadlines[Math.floor(Math.random() * subheadlines.length)] : f.subheadline || '';
      const bgImage = f.bg_image || f.section_image;

      return (
        <section className="relative overflow-hidden min-h-[600px] flex items-center" data-testid="section-hero">
          {bgImage ? (
            <>
              <div className="absolute inset-0">
                <img src={bgImage.startsWith('/api') ? `${API_BASE.replace('/api','')}${bgImage}` : bgImage} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70" />
              <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 30% 50%, rgba(26,157,165,0.15) 0%, transparent 50%)' }} />
            </>
          ) : (
            <>
              <div className="absolute inset-0 bg-gradient-to-br from-[#081E2B] via-[#0C2D3F] to-[#0F3D4D]" />
              <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 20% 50%, rgba(26,157,165,0.25) 0%, transparent 50%), radial-gradient(ellipse at 80% 30%, rgba(232,115,90,0.15) 0%, transparent 40%), radial-gradient(ellipse at 50% 100%, rgba(240,180,41,0.08) 0%, transparent 40%)' }} />
            </>
          )}

          {/* Decorative elements — always shown */}
          <div className="absolute top-10 left-[5%] w-72 h-72 rounded-full bg-[#1A9DA5]/8 blur-[100px] animate-pulse" />
          <div className="absolute bottom-10 right-[8%] w-60 h-60 rounded-full bg-[#E8735A]/8 blur-[80px]" style={{ animation: 'pulse 5s ease-in-out infinite' }} />
          <div className="absolute top-[20%] right-[5%] w-44 h-44 rounded-full bg-[#F0B429]/5 blur-[60px]" style={{ animation: 'pulse 7s ease-in-out infinite 1s' }} />

          {/* Concentric rings */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full border border-white/[0.03]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full border border-white/[0.04]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full border border-white/[0.03]" />

          {/* Dot grids */}
          <div className="absolute top-16 right-[15%] grid grid-cols-5 gap-2 opacity-20">
            {Array.from({ length: 15 }).map((_, i) => <div key={i} className="w-1.5 h-1.5 rounded-full bg-[#1A9DA5]" />)}
          </div>
          <div className="absolute bottom-20 left-[8%] grid grid-cols-4 gap-2 opacity-15">
            {Array.from({ length: 12 }).map((_, i) => <div key={i} className="w-1.5 h-1.5 rounded-full bg-[#F0B429]" />)}
          </div>
          <div className="absolute top-[30%] left-[3%] grid grid-cols-3 gap-1.5 opacity-10">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="w-1 h-1 rounded-full bg-[#E8735A]" />)}
          </div>

          {/* Diagonal lines */}
          <div className="absolute top-0 right-[25%] w-px h-40 bg-gradient-to-b from-transparent via-white/10 to-transparent rotate-[20deg]" />
          <div className="absolute bottom-0 left-[30%] w-px h-32 bg-gradient-to-b from-transparent via-[#1A9DA5]/20 to-transparent -rotate-[15deg]" />

          {/* Corner accents */}
          <div className="absolute top-8 left-8 w-16 h-16 border-l-2 border-t-2 border-white/[0.06] rounded-tl-2xl" />
          <div className="absolute bottom-8 right-8 w-16 h-16 border-r-2 border-b-2 border-white/[0.06] rounded-br-2xl" />

          {/* Floating shapes */}
          <div className="absolute top-[15%] right-[10%] w-20 h-20 rounded-2xl border border-white/[0.06] rotate-12" />
          <div className="absolute bottom-[20%] left-[6%] w-14 h-14 rounded-xl border border-[#1A9DA5]/10 -rotate-6" />
          <div className="absolute top-[60%] right-[20%] w-3 h-3 rounded-full bg-[#1A9DA5]/30" />
          <div className="absolute top-[25%] left-[18%] w-2 h-2 rounded-full bg-[#E8735A]/30" />
          <div className="absolute bottom-[35%] right-[35%] w-2 h-2 rounded-full bg-[#F0B429]/30" />

          <div className="relative max-w-5xl mx-auto px-4 py-24 md:py-28">
            <div className={`${f.include_payment_box ? 'grid md:grid-cols-5 gap-8 items-center' : 'max-w-3xl mx-auto text-center'} text-white`}>
              <div className={f.include_payment_box ? 'md:col-span-3' : ''}>
                {f.include_back_to_store && (
                  <div className="mb-4">
                    <Link to="/" className="inline-flex items-center gap-1 text-white/60 hover:text-white text-sm transition-colors">
                      <ArrowLeft className="w-4 h-4" />Browse other guides
                    </Link>
                  </div>
                )}
                {product?.category && (
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/10 text-sm mb-6">
                    <span className="w-2 h-2 rounded-full bg-[#1A9DA5] animate-pulse" />
                    <span className="text-white/90">{product.category}</span>
                  </div>
                )}
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight font-['Outfit'] mb-5 leading-[1.1]">
                  {headline}
                </h1>
                {subheadline && <p className={`text-lg text-white/65 max-w-2xl mb-8 leading-relaxed ${!f.include_payment_box ? 'mx-auto' : ''}`}>{subheadline}</p>}
                <div className={`flex flex-wrap items-center gap-3 ${!f.include_payment_box ? 'justify-center' : ''}`}>
                  {f.cta_text && (
                    <Button size="lg" className="bg-[#1A9DA5] hover:bg-[#158D92] text-white px-8 shadow-lg shadow-[#1A9DA5]/30 hover:shadow-xl hover:shadow-[#1A9DA5]/40 hover:-translate-y-0.5 transition-all duration-300 relative overflow-hidden group" onClick={onCheckout}>
                      <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                      {f.cta_text} <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  )}
                  {product?.teaser_audio_base64 && (
                    <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10 backdrop-blur gap-2 hover:-translate-y-0.5 transition-all duration-300" onClick={() => {
                      if (playingTeaser) { teaserRef.current?.pause(); setPlayingTeaser(false); }
                      else if (teaserRef.current) {
                        teaserRef.current.src = `data:audio/mp3;base64,${product.teaser_audio_base64}`;
                        teaserRef.current.play(); setPlayingTeaser(true);
                      }
                    }}>
                      {playingTeaser ? <Pause className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                      {playingTeaser ? 'Pause' : 'Listen Preview'}
                    </Button>
                  )}
                </div>
                <div className={`flex items-center gap-6 mt-8 text-sm text-white/40 ${!f.include_payment_box ? 'justify-center' : ''}`}>
                  <span className="flex items-center gap-1.5"><Headphones className="w-4 h-4 text-[#1A9DA5]" />Audio Guide</span>
                  {product?.duration_minutes && <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#E8735A]" />{product.duration_minutes} min</span>}
                  {product?.pdf_asset_id && <span className="flex items-center gap-1.5"><FileText className="w-4 h-4 text-[#E8735A]" />PDF Included</span>}
                  <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-[#F0B429]" />Lifetime Access</span>
                </div>
              </div>

              {/* Payment box in hero */}
              {f.include_payment_box && product && (
                <div className="md:col-span-2">
                  <Card className="border-2 border-white/10 bg-white/5 backdrop-blur shadow-2xl shadow-black/20">
                    <CardContent className="p-6 text-center">
                      {(() => {
                        const { hasDiscount, isFree, discounted, original } = getDisplayPrice(product);
                        if (isFree) return (<><p className="text-3xl font-bold text-[#1A9DA5] font-['Outfit'] mb-0.5">Free!</p>{original > 0 && <p className="text-white/40 line-through text-sm">${original}</p>}<p className="text-white/50 text-sm mb-4">{original > 0 ? 'limited time' : 'Free access'}</p></>);
                        if (hasDiscount) return (<><p className="text-3xl font-bold text-[#E8735A] font-['Outfit'] mb-0.5">${discounted.toFixed(2)}</p><p className="text-white/40 line-through text-sm">${original}</p><p className="text-white/50 text-sm mb-4">one-time payment</p></>);
                        return (<><p className="text-4xl font-bold text-white font-['Outfit'] mb-1">${original}</p><p className="text-white/50 text-sm mb-4">one-time payment</p></>);
                      })()}
                      <Button size="lg" className="w-full bg-[#1A9DA5] hover:bg-[#158D92] text-white shadow-lg shadow-[#1A9DA5]/25 transition-all hover:-translate-y-0.5" onClick={onCheckout}>
                        <ShoppingCart className="w-4 h-4 mr-2" /><PriceButton product={product} />
                      </Button>
                      <p className="text-white/40 text-xs mt-3">{getDisplayPrice(product).isFree ? 'Enter your email and get instant access. No payment needed, no spam.' : 'Instant access after purchase'}</p>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>

            {/* About Author hero panel */}
            {(() => {
              const authorSection = (allSections || []).find(s => s.type === 'about_author' && s.enabled && s.fields?.display_mode === 'hero_panel');
              if (!authorSection) return null;
              return (
                <div className="mt-8 max-w-2xl" data-testid="hero-about-author-panel">
                  <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5 flex items-start gap-4">
                    <img src="/thomas-white.png" alt="Thomas White" className="w-16 h-16 rounded-full object-cover border-2 border-white/20 shrink-0" />
                    <div className="text-white/80">
                      <p className="font-bold text-white text-sm font-['Outfit'] mb-1">Thomas White <span className="font-normal text-white/50">— About the Author</span></p>
                      <p className="text-xs leading-relaxed">Thomas White spent 18 years running a small service business before he understood the difference between being busy and actually moving forward. These guides are what he wishes someone had handed him in year two. No theory, no fluff. Just the things that actually helped.</p>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </section>
      );
    }

    case 'pain_points':
      return (
        <section className="py-14 px-4 bg-muted/30" data-testid="section-pain-points">
          <div className="max-w-3xl mx-auto">
            <Badge className="mb-3 bg-[#E8735A]/10 text-[#E8735A] border-[#E8735A]/20">{f.title || 'Sound Familiar?'}</Badge>
            <h2 className="text-2xl font-bold font-['Outfit'] mb-6">{f.title}</h2>
            {f.section_image && <img src={f.section_image.startsWith('/api') ? `${API_BASE.replace('/api','')}${f.section_image}` : f.section_image} alt="" className="w-full rounded-xl mb-6 max-h-64 object-cover" />}
            <div className="space-y-3">
              {(f.items || []).map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg border bg-background">
                  <span className="w-6 h-6 rounded-full bg-[#E8735A]/10 text-[#E8735A] flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                  <span className="text-sm">{typeof item === 'string' ? item : item.text || JSON.stringify(item)}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'bullets':
      return (
        <section className="py-14 px-4" data-testid="section-bullets">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold font-['Outfit'] mb-6">{f.title}</h2>
            {f.section_image && <img src={f.section_image.startsWith('/api') ? `${API_BASE.replace('/api','')}${f.section_image}` : f.section_image} alt="" className="w-full rounded-xl mb-6 max-h-64 object-cover" />}
            <div className="grid sm:grid-cols-2 gap-3">
              {(f.items || []).map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                  <CheckCircle className="w-5 h-5 text-[#1A9DA5] shrink-0 mt-0.5" />
                  <span className="text-sm">{typeof item === 'string' ? item : item.text || ''}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'what_youll_learn':
    case 'whats_included':
      return (
        <section className="py-14 px-4 bg-muted/30" data-testid="section-learn">
          <div className="max-w-3xl mx-auto">
            <Badge className="mb-3 bg-[#1A9DA5]/10 text-[#1A9DA5] border-[#1A9DA5]/20">What's Inside</Badge>
            <h2 className="text-2xl font-bold font-['Outfit'] mb-6">{f.title}</h2>
            {f.section_image && <img src={f.section_image.startsWith('/api') ? `${API_BASE.replace('/api','')}${f.section_image}` : f.section_image} alt="" className="w-full rounded-xl mb-6 max-h-64 object-cover" />}
            <div className="space-y-2">
              {(f.items || []).map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 rounded-lg bg-background border hover:border-[#1A9DA5]/30 transition-colors">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: i % 3 === 0 ? '#1A9DA510' : i % 3 === 1 ? '#E8735A10' : '#F0B42910' }}>
                    <CheckCircle className="w-4 h-4" style={{ color: i % 3 === 0 ? '#1A9DA5' : i % 3 === 1 ? '#E8735A' : '#F0B429' }} />
                  </div>
                  <span className="text-sm mt-0.5">{typeof item === 'string' ? item : item.text || ''}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'audio_teaser':
      return (
        <section className="py-14 px-4" data-testid="section-teaser">
          <div className="max-w-3xl mx-auto">
            <Card className="border-2 border-[#1A9DA5]/20 overflow-hidden">
              <div className="sparky-gradient p-5 flex items-center gap-3 text-white">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center"><Headphones className="w-5 h-5" /></div>
                <div>
                  <h2 className="text-lg font-bold font-['Outfit']">{f.title || 'Listen to a Preview'}</h2>
                  {f.description && <p className="text-white/70 text-xs">{f.description}</p>}
                </div>
              </div>
              <CardContent className="p-6 space-y-4">
                {product?.teaser_audio_base64 && (
                  <Button variant="outline" className="gap-2" onClick={() => {
                    if (playingTeaser) { teaserRef.current?.pause(); setPlayingTeaser(false); }
                    else if (teaserRef.current) {
                      teaserRef.current.src = `data:audio/mp3;base64,${product.teaser_audio_base64}`;
                      teaserRef.current.play(); setPlayingTeaser(true);
                    }
                  }} data-testid="landing-teaser-play-btn">
                    {playingTeaser ? <Pause className="w-4 h-4 text-[#1A9DA5]" /> : <Play className="w-4 h-4 text-[#1A9DA5]" />}
                    {playingTeaser ? 'Pause Preview' : 'Listen to Preview'}
                  </Button>
                )}
                {product?.teaser_script && <p className="text-sm text-muted-foreground italic leading-relaxed">{product.teaser_script}</p>}
                {f.audio_url && !product?.teaser_audio_base64 && <audio controls className="w-full mt-2" src={f.audio_url} />}
              </CardContent>
            </Card>
          </div>
        </section>
      );

    case 'testimonials':
      return (
        <section className="py-14 px-4 bg-muted/30" data-testid="section-testimonials">
          <div className="max-w-3xl mx-auto">
            <Badge className="mb-3 bg-[#F0B429]/10 text-[#F0B429] border-[#F0B429]/20">Testimonials</Badge>
            <h2 className="text-2xl font-bold font-['Outfit'] mb-6">{f.title}</h2>
            {f.section_image && <img src={f.section_image.startsWith('/api') ? `${API_BASE.replace('/api','')}${f.section_image}` : f.section_image} alt="" className="w-full rounded-xl mb-6 max-h-64 object-cover" />}
            <div className="grid sm:grid-cols-2 gap-4">
              {(f.items || []).map((t, i) => (
                <Card key={i} className="hover:border-[#F0B429]/30 transition-colors">
                  <CardContent className="p-5">
                    <div className="flex gap-1 mb-3">{[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 fill-[#F0B429] text-[#F0B429]" />)}</div>
                    <p className="text-sm italic mb-3 leading-relaxed">"{t.quote || t}"</p>
                    {t.name && <p className="text-xs font-semibold">{t.name}{t.business && <span className="text-muted-foreground font-normal"> — {t.business}</span>}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      );

    case 'pricing': {
      const dp = product?.discounted_price;
      const hasDiscount = dp !== null && dp !== undefined && dp >= 0;
      const isFree = hasDiscount && parseFloat(dp) === 0;
      const displayPrice = hasDiscount ? (isFree ? 0 : parseFloat(dp)) : parseFloat(product?.package_price || product?.price || f.price || 0);
      const originalPrice = parseFloat(product?.price || f.price || 0);
      const comparePrice = product?.compare_at_price || f.compare_price;
      return (
        <section className="py-16 px-4 relative overflow-hidden" id="pricing" data-testid="section-pricing">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#1A9DA5]/5 to-transparent" />
          <div className="relative max-w-md mx-auto">
            <Card className="border-2 border-[#1A9DA5]/30 text-center shadow-xl shadow-[#1A9DA5]/10 overflow-hidden">
              <div className="sparky-gradient p-4 text-white">
                <Sparkles className="w-6 h-6 mx-auto mb-1" />
                <h2 className="text-xl font-bold font-['Outfit']">{f.title}</h2>
              </div>
              <CardContent className="p-8 space-y-5">
                {isFree ? (<><div className="flex items-baseline justify-center gap-2"><span className="text-5xl font-bold font-['Outfit'] text-[#1A9DA5]">Free!</span></div>{originalPrice > 0 && <span className="text-lg text-muted-foreground line-through">${originalPrice.toFixed(2)}</span>}</>) :
                 hasDiscount ? (<><div className="flex items-baseline justify-center gap-2"><span className="text-5xl font-bold font-['Outfit'] text-[#E8735A]">${displayPrice.toFixed(2)}</span><span className="text-lg text-muted-foreground line-through">${originalPrice.toFixed(2)}</span></div><Badge className="bg-[#F0B429] text-white">Save ${(originalPrice - displayPrice).toFixed(0)}</Badge></>) :
                 (<><div className="flex items-baseline justify-center gap-2"><span className="text-5xl font-bold font-['Outfit']">${displayPrice.toFixed(2)}</span>{comparePrice && <span className="text-lg text-muted-foreground line-through">${comparePrice}</span>}</div>{comparePrice && <Badge className="bg-[#F0B429] text-white">Save ${(parseFloat(comparePrice) - displayPrice).toFixed(0)}</Badge>}</>)}
                {(f.features || []).length > 0 && (
                  <div className="space-y-2 text-left">
                    {f.features.map((feat, i) => <div key={i} className="flex items-center gap-2 text-sm"><CheckCircle className="w-4 h-4 text-[#1A9DA5] shrink-0" />{feat}</div>)}
                  </div>
                )}
                <Button onClick={onCheckout} className="w-full bg-[#1A9DA5] hover:bg-[#158D92] text-white py-6 text-lg font-semibold shadow-lg shadow-[#1A9DA5]/20 hover:-translate-y-0.5 transition-all">
                  <ShoppingCart className="w-5 h-5 mr-2" />{isFree ? 'Get It Free' : f.cta_text || 'Buy Now'}
                </Button>
                <p className="text-xs text-muted-foreground">{isFree ? 'Free · Instant access' : 'One-time payment · Instant access · Lifetime'}</p>
                {product?.pdf_asset_id && (
                  <div className="flex items-center justify-center gap-2 pt-2 border-t">
                    <FileText className="w-4 h-4 text-[#E8735A]" />
                    <span className="text-sm font-medium">PDF document included</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </section>
      );
    }

    case 'faq':
      return (
        <section className="py-14 px-4" data-testid="section-faq">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold font-['Outfit'] mb-6">{f.title}</h2>
            <Accordion type="single" collapsible>
              {(f.items || []).map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-sm text-left">{item.question || item}</AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">{item.answer || ''}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      );

    case 'how_it_works':
      return (
        <section className="py-16 px-4 bg-muted/30" data-testid="section-how-it-works">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-3 bg-[#E8735A]/10 text-[#E8735A] border-[#E8735A]/20">How It Works</Badge>
              <h2 className="text-3xl font-bold font-['Outfit']">{f.title || 'Get Started in 3 Easy Steps'}</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { step: 1, icon: Search, color: '#1A9DA5', title: 'Browse & Choose', desc: 'Explore our library of audio guides. Each guide is designed for a specific business challenge.' },
                { step: 2, icon: CreditCard, color: '#E8735A', title: 'Purchase Securely', desc: 'Complete your purchase through secure Stripe checkout. Get instant access via email.' },
                { step: 3, icon: Play, color: '#F0B429', title: 'Listen & Implement', desc: 'Access your guides anytime. Listen on the go and start implementing right away.' },
              ].map(s => (
                <div key={s.step} className="text-center">
                  <div className="relative mx-auto mb-4 w-16 h-16">
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${s.color}15` }}>
                      <s.icon className="w-7 h-7" style={{ color: s.color }} />
                    </div>
                    <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-foreground text-background text-xs font-bold flex items-center justify-center">{s.step}</div>
                  </div>
                  <h3 className="text-lg font-semibold font-['Outfit'] mb-2">{s.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'about_wayfinder':
      return (
        <section className="py-16 px-4" data-testid="section-about-sparky">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-5 gap-10 items-start">
              <div className="md:col-span-3">
                <Badge className="mb-3 bg-[#1A9DA5]/10 text-[#1A9DA5] border-[#1A9DA5]/20">About Wayfinder</Badge>
                <h2 className="text-3xl font-bold font-['Outfit'] mb-6">{f.title || "Built by someone who's been through it."}</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>My name is Thomas White. I ran a small service business for 18 years. I made most of the mistakes you're probably making right now, and I figured things out slowly, usually at some cost.</p>
                  <p>What I kept noticing was that good advice existed, but it was buried in long courses, expensive coaches, or generic blog posts that didn't actually apply to a real business with real pressures.</p>
                  <p>So I started writing things down. The stuff that actually worked. Short, practical, no fluff. That became Wayfinder Guides.</p>
                  <p className="font-medium text-foreground">These guides are not theory. They're what I'd tell a friend who just called me stressed about the same problem I already solved.</p>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-6">
                  {[{ icon: Headphones, text: 'Listen Anywhere' }, { icon: Clock, text: '5-30 Min Each' }, { icon: BookOpen, text: 'PDF Included' }, { icon: Shield, text: 'Lifetime Access' }].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm"><item.icon className="w-4 h-4 text-[#1A9DA5]" /><span>{item.text}</span></div>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2 flex flex-col items-center">
                <img src="/thomas-white.png" alt="Thomas White" className="w-48 h-48 rounded-full object-cover border-4 border-[#1A9DA5]/20 shadow-xl mb-4" />
                <h3 className="text-lg font-bold font-['Outfit'] text-center">Thomas White</h3>
                <p className="text-sm text-muted-foreground text-center">Founder, Wayfinder Guides</p>
                <div className="mt-4 space-y-2 w-full">
                  {['18 years running a small business', 'Practical advice, not theory', 'New guides added regularly'].map((t, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm"><Sparkles className="w-3.5 h-3.5 text-[#E8735A] shrink-0" /><span>{t}</span></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      );

    case 'guarantee':
      return (
        <section className="py-14 px-4 bg-muted/30" data-testid="section-guarantee">
          <div className="max-w-3xl mx-auto text-center">
            <Shield className="w-10 h-10 mx-auto mb-3 text-[#1A9DA5]" />
            <h2 className="text-2xl font-bold font-['Outfit'] mb-3">{f.title}</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">{f.description}</p>
          </div>
        </section>
      );

    case 'custom_rich_text':
      return (
        <section className="py-14 px-4" data-testid="section-custom">
          <div className="max-w-3xl mx-auto">
            {f.title && <h2 className="text-2xl font-bold font-['Outfit'] mb-4">{f.title}</h2>}
            <div className="prose prose-sm dark:prose-invert max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" dangerouslySetInnerHTML={{ __html: f.content || '' }} />
          </div>
        </section>
      );

    case 'external_link':
      if (!product?.external_link_url) return null;
      return (
        <section className="py-10 px-4" data-testid="section-external-link">
          <div className="max-w-3xl mx-auto">
            <Card className="border-2 border-[#1A9DA5]/20">
              <CardContent className="p-6 flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
                <div className="w-10 h-10 rounded-lg bg-[#1A9DA5]/10 flex items-center justify-center shrink-0">
                  <ExternalLink className="w-5 h-5 text-[#1A9DA5]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold font-['Outfit']">{product.external_link_label || 'Recommended Resource'}</h3>
                  <p className="text-sm text-muted-foreground">{f.description || 'Companion tool for this guide'}</p>
                </div>
                <a href={product.external_link_url} target="_blank" rel="noopener noreferrer" className="shrink-0" data-testid="landing-external-link-btn">
                  <Button className="bg-[#1A9DA5] hover:bg-[#158D92] text-white whitespace-nowrap">
                    <ExternalLink className="w-4 h-4 mr-2" />{product.external_link_label || 'Recommended Resource'}
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </section>
      );

    case 'problem_agitation':
      return (
        <section className="py-16 px-4 bg-muted/30" data-testid="section-problem-agitation">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold font-['Outfit'] mb-8">{f.title}</h2>
            <div className="space-y-4">
              {(f.items || []).map((item, i) => (
                <div key={i} className="p-5 rounded-xl border bg-background text-left flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#E8735A]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-[#E8735A] font-bold text-sm">{i + 1}</span>
                  </div>
                  <p className="text-sm leading-relaxed">{typeof item === 'string' ? item : item.text || ''}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'solution_mechanism':
      return (
        <section className="py-16 px-4" data-testid="section-solution-mechanism">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold font-['Outfit'] mb-4 text-center">{f.title}</h2>
            {f.description && <p className="text-base text-muted-foreground text-center mb-8 max-w-2xl mx-auto leading-relaxed">{f.description}</p>}
            {(f.items || []).length > 0 && (
              <div className="space-y-3">
                {(f.items || []).map((item, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 rounded-xl border bg-background hover:border-[#1A9DA5]/30 transition-colors">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: i % 3 === 0 ? '#1A9DA510' : i % 3 === 1 ? '#E8735A10' : '#F0B42910' }}>
                      <CheckCircle className="w-4 h-4" style={{ color: i % 3 === 0 ? '#1A9DA5' : i % 3 === 1 ? '#E8735A' : '#F0B429' }} />
                    </div>
                    <span className="text-sm leading-relaxed mt-1">{typeof item === 'string' ? item : item.text || ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      );

    case 'social_proof':
      return (
        <section className="py-16 px-4 bg-muted/30" data-testid="section-social-proof">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold font-['Outfit'] mb-4">{f.title}</h2>
            {f.stats && <p className="text-lg text-[#1A9DA5] font-semibold mb-8">{f.stats}</p>}
            {(f.items || []).length > 0 && (
              <div className="grid sm:grid-cols-2 gap-4 text-left">
                {(f.items || []).map((item, i) => (
                  <Card key={i} className="hover:border-[#1A9DA5]/30 transition-colors">
                    <CardContent className="p-5">
                      <div className="flex gap-1 mb-2">{[1,2,3,4,5].map(s => <Star key={s} className="w-3.5 h-3.5 fill-[#F0B429] text-[#F0B429]" />)}</div>
                      <p className="text-sm italic mb-2 leading-relaxed">"{typeof item === 'string' ? item : item.quote || item.text || ''}"</p>
                      {item.name && <p className="text-xs font-semibold">{item.name}{item.business && <span className="text-muted-foreground font-normal"> — {item.business}</span>}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>
      );

    case 'offer_stack': {
      const valueStack = f.value_stack || [];
      const bonuses = f.bonuses || [];
      return (
        <section className="py-16 px-4 relative overflow-hidden" data-testid="section-offer-stack">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#1A9DA5]/5 to-transparent" />
          <div className="relative max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold font-['Outfit'] mb-8 text-center">{f.title}</h2>
            <Card className="border-2 border-[#1A9DA5]/30 shadow-xl overflow-hidden">
              {valueStack.length > 0 && (
                <div className="p-6 space-y-3">
                  {valueStack.map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-[#1A9DA5] shrink-0" />
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              )}
              {f.price_anchor && (
                <div className="px-6 py-3 bg-muted/50 text-center">
                  <p className="text-sm text-muted-foreground line-through">{f.price_anchor}</p>
                </div>
              )}
              {bonuses.length > 0 && (
                <div className="p-6 border-t space-y-3">
                  <p className="text-sm font-bold text-[#E8735A] uppercase tracking-wide">Bonuses Included</p>
                  {bonuses.map((b, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Sparkles className="w-4 h-4 text-[#F0B429] shrink-0" />
                      <span className="text-sm">{b}</span>
                    </div>
                  ))}
                </div>
              )}
              {(f.guarantee_title || f.guarantee_text) && (
                <div className="p-6 border-t bg-emerald-500/5">
                  <div className="flex items-start gap-3">
                    <Shield className="w-6 h-6 text-emerald-500 shrink-0 mt-0.5" />
                    <div>
                      {f.guarantee_title && <p className="font-bold text-sm mb-1">{f.guarantee_title}</p>}
                      {f.guarantee_text && <p className="text-sm text-muted-foreground">{f.guarantee_text}</p>}
                    </div>
                  </div>
                </div>
              )}
              {(f.scarcity || f.urgency) && (
                <div className="p-4 border-t bg-[#E8735A]/5 text-center space-y-1">
                  {f.scarcity && <p className="text-sm font-semibold text-[#E8735A]">{f.scarcity}</p>}
                  {f.urgency && <p className="text-xs text-muted-foreground">{f.urgency}</p>}
                </div>
              )}
              <div className="p-6 border-t">
                <Button onClick={onCheckout} className="w-full bg-[#1A9DA5] hover:bg-[#158D92] text-white py-6 text-lg font-semibold shadow-lg shadow-[#1A9DA5]/20 hover:-translate-y-0.5 transition-all">
                  <ShoppingCart className="w-5 h-5 mr-2" />{f.cta_text || 'Get Instant Access'}
                </Button>
              </div>
            </Card>
          </div>
        </section>
      );
    }

    case 'final_cta':
      return (
        <section className="py-20 px-4 relative overflow-hidden" data-testid="section-final-cta">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0C2D3F] via-[#0F3D4D] to-[#1A4A5A]" />
          <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 50% 50%, rgba(26,157,165,0.2) 0%, transparent 60%)' }} />
          <div className="relative max-w-2xl mx-auto text-center text-white">
            <h2 className="text-3xl sm:text-4xl font-bold font-['Outfit'] mb-4">{f.title}</h2>
            {f.description && <p className="text-lg text-white/60 mb-8 leading-relaxed">{f.description}</p>}
            <Button size="lg" className="bg-[#1A9DA5] hover:bg-[#158D92] text-white px-10 py-6 text-lg shadow-lg shadow-[#1A9DA5]/30 hover:-translate-y-0.5 transition-all" onClick={onCheckout}>
              {f.cta_text || 'Get Started Now'} <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </section>
      );

    case 'pdf_content':
      return (
        <section className="py-14 px-4" data-testid="section-pdf-content">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-6 h-6 text-[#E8735A]" />
              <h2 className="text-2xl font-bold font-['Outfit']">{f.title}</h2>
            </div>
            {f.description && <p className="text-base text-muted-foreground mb-6 leading-relaxed">{f.description}</p>}
            {(f.page_count || f.format) && (
              <div className="flex items-center gap-3 mb-6">
                {f.format && <Badge variant="outline" className="bg-[#E8735A]/10 text-[#E8735A]">{f.format}</Badge>}
                {f.page_count && <Badge variant="outline">{f.page_count} pages</Badge>}
              </div>
            )}
            {(f.items || []).length > 0 && (
              <div className="space-y-2">
                {(f.items || []).map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg border hover:border-[#1A9DA5]/30 transition-colors">
                    <CheckCircle className="w-4 h-4 text-[#1A9DA5] shrink-0 mt-0.5" />
                    <span className="text-sm">{typeof item === 'string' ? item : item.text || ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      );

    case 'about_author': {
      const isPanel = f.display_mode === 'hero_panel';
      if (isPanel) return null; // Rendered inside hero section instead
      return (
        <section className="py-14 px-4 bg-muted/30" data-testid="section-about-author">
          <div className="max-w-3xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start gap-6">
              <img src="/thomas-white.png" alt="Thomas White" className="w-28 h-28 rounded-full object-cover border-4 border-[#1A9DA5]/20 shadow-lg shrink-0" />
              <div>
                <Badge className="mb-2 bg-[#1A9DA5]/10 text-[#1A9DA5] border-[#1A9DA5]/20">About the Author</Badge>
                <h3 className="text-xl font-bold font-['Outfit'] mb-3">Thomas White</h3>
                <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
                  <p>Thomas White spent 18 years running a small service business before he understood the difference between being busy and actually moving forward. He made most of the classic mistakes — undercharging, inconsistent marketing, cash always feeling tighter than it should have been — and figured things out slowly, usually the expensive way.</p>
                  <p className="font-medium text-foreground">These guides are what he wishes someone had handed him in year two. No theory, no fluff. Just the things that actually helped, written down clearly enough to act on the same week.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      );
    }

    case 'category_featured': {
      const catFeatured = (allProducts || []).filter(p => p.category_id === f.category_id && p.featured_in_category);
      if (catFeatured.length === 0) return null;
      return (
        <section className="py-14 px-4 bg-muted/30" data-testid="section-category-featured">
          <div className="max-w-5xl mx-auto">
            {f.title && <h2 className="text-2xl font-bold font-['Outfit'] mb-6 flex items-center gap-2"><Star className="w-5 h-5 text-[#E8735A]" />{f.title}</h2>}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {catFeatured.map(p => (
                <Card key={p.id} className="group border-2 border-[#E8735A]/10 hover:border-[#E8735A]/40 hover:shadow-lg transition-all cursor-pointer relative overflow-hidden">
                  <a href={`/${p.type === 'package' ? 'packages' : 'guides'}/${p.slug}`} className="block">
                    <div className="absolute top-3 right-3"><Badge className="bg-[#E8735A] text-white text-[10px]"><Star className="w-2.5 h-2.5 mr-0.5 fill-white" />Featured</Badge></div>
                    <CardContent className="p-5">
                      <div className="w-10 h-10 rounded-lg bg-[#E8735A]/10 flex items-center justify-center mb-3">
                        {p.type === 'package' ? <Package className="w-5 h-5 text-[#E8735A]" /> : <Headphones className="w-5 h-5 text-[#E8735A]" />}
                      </div>
                      <h3 className="text-base font-semibold font-['Outfit'] mb-1 group-hover:text-[#E8735A] transition-colors pr-16">{p.title}</h3>
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{p.short_description}</p>
                      <div className="flex items-center justify-between">
                        <PriceDisplay product={p} size="sm" />
                        {p.duration_minutes && (
                          <div className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${durationColor(p.duration_minutes).bg} ${durationColor(p.duration_minutes).text}`}>
                            <Clock className="w-3 h-3" />{p.duration_minutes} min
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </a>
                </Card>
              ))}
            </div>
          </div>
        </section>
      );
    }

    case 'product_spotlight': {
      const spotProduct = (allProducts || []).find(p => p.id === f.product_id);
      if (!spotProduct) return null;
      const navigate = window.__spotlightNav; // we'll use a link instead
      return (
        <section className="py-12 px-4" data-testid="section-product-spotlight">
          <div className="max-w-3xl mx-auto">
            {f.title && <h2 className="text-xl font-bold font-['Outfit'] mb-4">{f.title}</h2>}
            <Card className="border-2 border-[#1A9DA5]/20 hover:border-[#1A9DA5]/40 transition-all overflow-hidden">
              <div className="p-6 flex flex-col sm:flex-row gap-5">
                <div className="w-14 h-14 rounded-xl bg-[#1A9DA5]/10 flex items-center justify-center shrink-0">
                  <Headphones className="w-7 h-7 text-[#1A9DA5]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold font-['Outfit'] mb-1">{spotProduct.title}</h3>
                  {spotProduct.subtitle && <p className="text-sm text-muted-foreground mb-2">{spotProduct.subtitle}</p>}
                  {spotProduct.short_description && <p className="text-sm text-muted-foreground mb-3 line-clamp-3">{spotProduct.short_description}</p>}
                  {spotProduct.outcomes?.length > 0 && (
                    <div className="space-y-1 mb-3">
                      {spotProduct.outcomes.slice(0, 3).map((o, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm"><CheckCircle className="w-3.5 h-3.5 text-[#1A9DA5] shrink-0" />{o}</div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="text-xl font-bold font-['Outfit'] text-[#1A9DA5]">${spotProduct.discounted_price != null ? spotProduct.discounted_price : spotProduct.price}</span>
                    {spotProduct.discounted_price != null && <span className="text-sm text-muted-foreground line-through">${spotProduct.price}</span>}
                    {spotProduct.duration_minutes && <Badge variant="outline"><Clock className="w-3 h-3 mr-1" />{spotProduct.duration_minutes} min</Badge>}
                    <a href={`/${spotProduct.type === 'package' ? 'packages' : 'guides'}/${spotProduct.slug}`}>
                      <Button size="sm" className="bg-[#1A9DA5] hover:bg-[#158D92] text-white">
                        View Details <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </section>
      );
    }

    default:
      return null;
  }
}

export default function LandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [page, setPage] = useState(null);
  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [playingTeaser, setPlayingTeaser] = useState(false);
  const teaserRef = useRef(null);

  useEffect(() => {
    api.get(`/public/pages/slug/${slug}`).then(r => {
      setPage(r.data);
      if (r.data.product_id) {
        api.get('/public/products').then(pr => {
          setAllProducts(pr.data);
          const p = pr.data.find(prod => prod.id === r.data.product_id);
          if (p) {
            setProduct(p);
            // Meta Pixel: ViewContent on landing page
            if (window.fbq) window.fbq('track', 'ViewContent', { content_name: p.title, content_ids: [p.id], content_type: 'product', value: p.discounted_price ?? p.price, currency: 'USD' });
          }
        }).catch(() => {});
      } else {
        api.get('/public/products').then(pr => setAllProducts(pr.data)).catch(() => {});
      }
    }).catch(() => navigate('/'));
    api.post('/analytics/event', { event_type: 'page_view', page_id: '', metadata: { slug } }).catch(() => {});
  }, [slug, navigate]);

  if (!page) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-[#1A9DA5] border-t-transparent rounded-full" /></div>;

  const handleCheckout = () => {
    if (product) navigate(`/checkout?product_id=${product.id}`);
  };

  const sections = (page.sections || []).filter(s => s.enabled).sort((a, b) => a.order - b.order);

  return (
    <div className="min-h-screen" data-testid="landing-page">
      <audio ref={teaserRef} onEnded={() => setPlayingTeaser(false)} />
      <header className="sticky top-0 z-50 border-b glass-effect bg-background/80">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><img src="/logo.png" alt="Wayfinder" className="w-6 h-6 rounded" /><span className="font-bold font-['Outfit']">Wayfinder</span></Link>
          <Button variant="ghost" size="icon" onClick={toggleTheme}>{theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</Button>
        </div>
      </header>
      {sections.map(s => <RenderSection key={s.id} section={s} product={product} allProducts={allProducts} allSections={sections} onCheckout={handleCheckout} teaserRef={teaserRef} playingTeaser={playingTeaser} setPlayingTeaser={setPlayingTeaser} />)}
      <Footer />
    </div>
  );
}
